import { cloneElement, ReactElement, ReactNode } from 'react';

interface StatCardProps {
  title?: string;
  value: string | number | ReactNode;
  icon?: ReactElement;
  loading?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  titleClassName?: string;
  valueClassName?: string;
}

const sizeClasses = {
  sm: {
    icon: 'h-5 w-5',
    text: 'text-sm',
    value: 'text-base',
    padding: 'p-3',
    gap: 'mr-3',
  },
  md: {
    icon: 'h-7 w-7',
    text: 'text-base',
    value: 'text-lg',
    padding: 'p-4',
    gap: 'mr-4',
  },
  lg: {
    icon: 'h-9 w-9',
    text: 'text-lg',
    value: 'text-xl',
    padding: 'p-5',
    gap: 'mr-5',
  },
};

const StatCard = ({
  title,
  value,
  icon,
  loading,
  size = 'md',
  className,
  titleClassName = 'text-gray-500',
  valueClassName,
}: StatCardProps) => {
  const isPrimitive = typeof value === 'string' || typeof value === 'number';

  const iconWithSize =
    icon &&
    cloneElement(icon, {
      className: `${sizeClasses[size].icon} ${icon.props.className ?? ''}`,
    });

  return (
    <div
      className={`min-w-0 bg-white shadow-sm border rounded-xl flex items-start ${sizeClasses[size].padding} ${
        className ?? ''
      }`}
    >
      {iconWithSize && (
        <div className={`mt-2 shrink-0 ${sizeClasses[size].gap}`}>{iconWithSize}</div>
      )}

      <div className="min-w-0 flex-1">
        {title && <p className={`${titleClassName} ${sizeClasses[size].text}`}>{title}</p>}

        {loading ? (
          <div className="h-5 w-20 bg-gray-200 animate-pulse rounded mt-1" />
        ) : (
          <div
            className={
              isPrimitive
                ? `${sizeClasses[size].value} font-semibold mt-1 ${valueClassName ?? ''}`
                : 'mt-1'
            }
          >
            {value}
          </div>
        )}
      </div>
    </div>
  );
};

export default StatCard;
